/**
 * Clerk provisioning for a scaffolded storefront.
 *
 * Creating a Clerk application is only possible through the Platform API, whose
 * `platform_api_access_token` is issued to partners/resellers — it is not
 * self-serve. So provisioning runs in three tiers, best first:
 *
 *   1. `platform`   — CLERK_PLATFORM_API_KEY present: create a real application
 *                     and read its instance keys back. Fully automatic.
 *   2. `discovered` — reuse a pk_/sk_ pair already on the box (env or .env).
 *                     Automatic, but reuses an existing instance.
 *   3. `manual`     — nothing to go on: return the exact dashboard steps.
 *
 * Every tier ends at the same place: a publishable/secret pair that the caller
 * writes into `.env.local` and pushes to the deploy target. Instance settings
 * (allowed origins, redirect URLs) are then applied over the Backend API, which
 * works with an ordinary secret key regardless of which tier produced it.
 *
 * Endpoints below are taken from Clerk's published OpenAPI specs
 * (github.com/clerk/openapi-specs): `platform/beta.yml` and `bapi/2026-05-12.yml`.
 */

const CLERK_API_BASE = "https://api.clerk.com/v1";

export type ClerkTier = "platform" | "cli-keyless" | "discovered" | "manual";

export interface ClerkCredentials {
  publishableKey: string;
  secretKey: string;
  tier: ClerkTier;
  applicationId?: string;
  instanceId?: string;
}

export interface ClerkProvisionResult {
  ok: boolean;
  tier: ClerkTier;
  credentials?: ClerkCredentials;
  /** Why the tier failed, or why we fell through to a lower one. */
  reason?: string;
  /** Operator-facing steps, populated for the `manual` tier. */
  guidance?: string[];
}

export interface ClerkConfigureResult {
  ok: boolean;
  applied: string[];
  failed: Array<{ step: string; reason: string }>;
}

type Env = Record<string, string | undefined>;

const PUBLISHABLE_KEYS = [
  "CLERK_PUBLISHABLE_KEY",
  "NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY",
] as const;
const SECRET_KEYS = ["CLERK_SECRET_KEY"] as const;

function firstNonEmpty(env: Env, names: readonly string[]): string | undefined {
  for (const name of names) {
    const value = env[name]?.trim();
    if (value) return value;
  }
  return undefined;
}

/** Shape check only — never proves the key is live. */
export function looksPublishable(key: string): boolean {
  return /^pk_(test|live)_/.test(key);
}

export function looksSecret(key: string): boolean {
  return /^sk_(test|live)_/.test(key);
}

async function clerkFetch(
  path: string,
  token: string,
  init: { method?: string; body?: unknown } = {},
): Promise<{ ok: boolean; status: number; json?: unknown; error?: string }> {
  try {
    const res = await fetch(`${CLERK_API_BASE}${path}`, {
      method: init.method ?? "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
    const text = await res.text();
    let json: unknown;
    try {
      json = text ? JSON.parse(text) : undefined;
    } catch {
      json = undefined;
    }
    if (!res.ok) {
      return { ok: false, status: res.status, json, error: clerkError(json) ?? text.slice(0, 300) };
    }
    return { ok: true, status: res.status, json };
  } catch (e: unknown) {
    return { ok: false, status: 0, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Clerk returns `{ errors: [{ message, long_message }] }` on failure. */
function clerkError(json: unknown): string | undefined {
  if (typeof json !== "object" || json === null) return undefined;
  const errors = (json as { errors?: unknown }).errors;
  if (!Array.isArray(errors) || errors.length === 0) return undefined;
  const first = errors[0] as { message?: string; long_message?: string };
  return first.long_message ?? first.message;
}

interface PlatformInstance {
  instance_id: string;
  environment_type: "development" | "production";
  secret_key?: string;
  publishable_key?: string;
}

/**
 * Tier 1 — create a real Clerk application via the Platform API.
 *
 * `POST /platform/applications` returns the application with its instances,
 * each carrying `publishable_key` / `secret_key`.
 */
export async function createClerkApplication(
  platformToken: string,
  opts: { name: string; domain?: string; production?: boolean },
): Promise<ClerkProvisionResult> {
  const body: Record<string, unknown> = {
    name: opts.name,
    environment_types: opts.production ? ["development", "production"] : ["development"],
  };
  if (opts.domain) body.domain = opts.domain;

  const res = await clerkFetch("/platform/applications", platformToken, {
    method: "POST",
    body,
  });
  if (!res.ok) {
    return {
      ok: false,
      tier: "platform",
      reason:
        res.status === 401 || res.status === 403
          ? `Clerk Platform API rejected the token (HTTP ${res.status}). Check CLERK_PLATFORM_API_KEY.`
          : `Clerk Platform API error: ${res.error ?? `HTTP ${res.status}`}`,
    };
  }

  const payload = res.json as
    | { application_id?: string; instances?: PlatformInstance[] }
    | undefined;
  const instances = payload?.instances ?? [];
  // Prefer production when it was requested and came back with keys.
  const wanted = opts.production ? "production" : "development";
  const instance =
    instances.find((i) => i.environment_type === wanted && i.secret_key && i.publishable_key) ??
    instances.find((i) => i.secret_key && i.publishable_key);

  if (!instance?.secret_key || !instance.publishable_key) {
    return {
      ok: false,
      tier: "platform",
      reason:
        "Clerk created the application but returned no instance keys. Read them with " +
        "GET /platform/applications?include_secret_keys=true.",
    };
  }

  return {
    ok: true,
    tier: "platform",
    credentials: {
      publishableKey: instance.publishable_key,
      secretKey: instance.secret_key,
      tier: "platform",
      applicationId: payload?.application_id,
      instanceId: instance.instance_id,
    },
  };
}

/**
 * Tier 2 — mint a keyless application with the Clerk CLI.
 *
 * `clerk init` run while signed out provisions a claimable application and
 * writes real dev keys, with no account and no credential of any kind. That is
 * the only way to create a Clerk application without a partner token: the
 * published spec exposes `/platform/accountless_applications/claim` but no
 * corresponding create endpoint, so there is nothing to call directly.
 *
 * The CLI also rewrites source. Verified against this template: it wraps
 * `layout.tsx` in a second `<ClerkProvider>` even though `Providers.tsx`
 * already mounts one with our appearance config, reindents the file, and drops
 * the trailing newline. So everything it touches is snapshotted and restored;
 * only `.env.local` is kept. `--no-skills` suppresses a global agent-skills
 * install that otherwise lands outside the project entirely.
 */
export async function mintKeylessClerkApp(
  storefront: string,
  deps: ClerkCliDeps,
): Promise<ClerkProvisionResult> {
  if (!(await deps.which("npx"))) {
    return { ok: false, tier: "cli-keyless", reason: "npx not found — cannot run the Clerk CLI." };
  }

  const restore = await deps.snapshot(storefront);
  try {
    const r = await deps.run(
      "npx",
      ["--yes", "clerk@latest", "init", "--framework", "next", "--keyless", "--no-skills", "-y"],
      { cwd: storefront, timeoutMs: 600_000 },
    );
    // Read the keys before restoring, since .env.local is what we are keeping.
    const envPath = `${storefront}/.env.local`;
    const body = (await deps.readFileIfExists(envPath)) ?? "";
    const publishableKey = matchEnv(body, "NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY");
    const secretKey = matchEnv(body, "CLERK_SECRET_KEY");

    if (!publishableKey || !secretKey) {
      const tail = `${r.stdout}${r.stderr}`.trim().split("\n").slice(-2).join(" ").slice(0, 300);
      return {
        ok: false,
        tier: "cli-keyless",
        reason: `Clerk CLI did not produce keys${tail ? `: ${tail}` : "."}`,
      };
    }
    if (!looksPublishable(publishableKey) || !looksSecret(secretKey)) {
      return { ok: false, tier: "cli-keyless", reason: "Clerk CLI wrote keys in an unexpected format." };
    }

    return {
      ok: true,
      tier: "cli-keyless",
      credentials: { publishableKey, secretKey, tier: "cli-keyless" },
    };
  } finally {
    // Undo every source edit the CLI made; keep only .env.local.
    await restore();
  }
}

/**
 * Pull production keys for an already-linked application.
 *
 * A keyless or development instance is not something to point a real storefront
 * at, so this is opt-in and separate. It needs an authenticated CLI session
 * (`clerk auth login`) and a linked app — the keys land in `.env.local`.
 */
export async function pullProductionKeys(
  storefront: string,
  deps: ClerkCliDeps,
): Promise<ClerkProvisionResult> {
  if (!(await deps.which("npx"))) {
    return { ok: false, tier: "platform", reason: "npx not found — cannot run the Clerk CLI." };
  }
  const r = await deps.run("npx", ["--yes", "clerk@latest", "env", "pull", "--instance", "prod"], {
    cwd: storefront,
    timeoutMs: 300_000,
  });
  if (!r.ok) {
    const tail = `${r.stdout}${r.stderr}`.trim().split("\n").slice(-2).join(" ").slice(0, 300);
    return {
      ok: false,
      tier: "platform",
      reason: `Could not pull production keys${tail ? `: ${tail}` : "."} Run \`clerk auth login\` and link the app first.`,
    };
  }
  const body = (await deps.readFileIfExists(`${storefront}/.env.local`)) ?? "";
  const publishableKey = matchEnv(body, "NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY");
  const secretKey = matchEnv(body, "CLERK_SECRET_KEY");
  if (!publishableKey || !secretKey) {
    return { ok: false, tier: "platform", reason: "Clerk CLI reported success but wrote no keys." };
  }
  return {
    ok: true,
    tier: "platform",
    credentials: { publishableKey, secretKey, tier: "platform" },
  };
}

function matchEnv(body: string, key: string): string | undefined {
  const line = body.split("\n").find((l) => l.trim().startsWith(`${key}=`));
  const value = line?.slice(line.indexOf("=") + 1).trim();
  return value || undefined;
}

/**
 * The bits of the outside world `mintKeylessClerkApp` needs.
 *
 * Injected rather than imported so the tier is testable without spawning a real
 * CLI or writing to a real storefront.
 */
export interface ClerkCliDeps {
  which(bin: string): Promise<boolean>;
  run(
    cmd: string,
    args: string[],
    opts: { cwd?: string; timeoutMs?: number },
  ): Promise<{ ok: boolean; stdout: string; stderr: string }>;
  readFileIfExists(path: string): Promise<string | undefined>;
  /** Snapshot everything the CLI may rewrite; returns the restore function. */
  snapshot(storefront: string): Promise<() => Promise<void>>;
}

/** Tier 3 — a usable pk_/sk_ pair already present in the environment. */
export function discoverClerkCredentials(env: Env): ClerkCredentials | undefined {
  const publishableKey = firstNonEmpty(env, PUBLISHABLE_KEYS);
  const secretKey = firstNonEmpty(env, SECRET_KEYS);
  if (!publishableKey || !secretKey) return undefined;
  if (!looksPublishable(publishableKey) || !looksSecret(secretKey)) return undefined;
  return { publishableKey, secretKey, tier: "discovered" };
}

const MANUAL_STEPS = [
  "Open https://dashboard.clerk.com and create an application.",
  "Copy the Publishable key (pk_...) and Secret key (sk_...) from API keys.",
  "Put them in the storefront's .env.local as NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY and CLERK_SECRET_KEY.",
  "Re-run `carrier site deploy` — the keys are pushed to the deploy target automatically.",
];

/**
 * Run the tiers in order and return the first that produces credentials.
 *
 * Never throws: a failed higher tier degrades to the next one, carrying its
 * reason forward so the operator can see why the better path did not run.
 */
export async function provisionClerk(opts: {
  name: string;
  domain?: string;
  production?: boolean;
  env: Env;
  /** Skip every creating tier; only reuse credentials that already exist. */
  noCreate?: boolean;
  /** Storefront directory — required for the keyless tier. */
  storefront?: string;
  /** Omit to disable the keyless tier (it needs to run a CLI). */
  cli?: ClerkCliDeps;
}): Promise<ClerkProvisionResult> {
  const platformToken = firstNonEmpty(opts.env, [
    "CLERK_PLATFORM_API_KEY",
    "CLERK_PLATFORM_TOKEN",
  ]);

  const notes: string[] = [];

  if (platformToken && !opts.noCreate) {
    const created = await createClerkApplication(platformToken, {
      name: opts.name,
      domain: opts.domain,
      production: opts.production,
    });
    if (created.ok) return created;
    notes.push(created.reason ?? "Clerk Platform API call failed.");
  }

  // Reusing keys that already exist beats minting a throwaway app, so the
  // keyless tier only runs when there is nothing to reuse.
  const discovered = discoverClerkCredentials(opts.env);
  if (discovered) {
    return {
      ok: true,
      tier: "discovered",
      credentials: discovered,
      reason: notes.length ? notes.join(" ") : undefined,
    };
  }

  if (opts.cli && opts.storefront && !opts.noCreate) {
    const minted = await mintKeylessClerkApp(opts.storefront, opts.cli);
    if (minted.ok) {
      return { ...minted, reason: notes.length ? notes.join(" ") : undefined };
    }
    notes.push(minted.reason ?? "Clerk CLI keyless provisioning failed.");
  }

  if (!platformToken) {
    notes.push(
      "No CLERK_PLATFORM_API_KEY set, so a new Clerk application cannot be created through the " +
        "Platform API (it is a partner surface, not self-serve).",
    );
  }

  return {
    ok: false,
    tier: "manual",
    reason: notes.join(" "),
    guidance: MANUAL_STEPS,
  };
}

/**
 * Apply instance settings over the Backend API.
 *
 * Works with any secret key, whichever tier produced it:
 *   PATCH /instance      — allowed_origins
 *   POST  /redirect_urls — one call per URL
 *
 * Partial success is normal (a URL may already be whitelisted), so each step is
 * reported separately rather than collapsing to a single boolean.
 */
export async function configureClerkInstance(
  secretKey: string,
  opts: { allowedOrigins?: string[]; redirectUrls?: string[] },
): Promise<ClerkConfigureResult> {
  const applied: string[] = [];
  const failed: Array<{ step: string; reason: string }> = [];

  const origins = dedupe(opts.allowedOrigins ?? []);
  if (origins.length > 0) {
    const res = await clerkFetch("/instance", secretKey, {
      method: "PATCH",
      body: { allowed_origins: origins },
    });
    if (res.ok) applied.push(`allowed_origins (${origins.length})`);
    else failed.push({ step: "allowed_origins", reason: res.error ?? `HTTP ${res.status}` });
  }

  for (const url of dedupe(opts.redirectUrls ?? [])) {
    const res = await clerkFetch("/redirect_urls", secretKey, { method: "POST", body: { url } });
    if (res.ok) applied.push(`redirect_url ${url}`);
    else failed.push({ step: `redirect_url ${url}`, reason: res.error ?? `HTTP ${res.status}` });
  }

  return { ok: failed.length === 0, applied, failed };
}

function dedupe(values: string[]): string[] {
  return [...new Set(values.map((v) => v.trim()).filter(Boolean))];
}

/** Origins and redirect URLs a storefront needs, given where it ended up. */
export function storefrontClerkUrls(deployedUrl?: string, domain?: string): {
  allowedOrigins: string[];
  redirectUrls: string[];
} {
  const bases = dedupe([
    deployedUrl?.replace(/\/$/, "") ?? "",
    domain ? `https://${domain.replace(/^https?:\/\//, "").replace(/\/$/, "")}` : "",
    "http://localhost:3000",
  ]);
  return {
    allowedOrigins: bases,
    redirectUrls: bases.flatMap((b) => [b, `${b}/checkout/success`, `${b}/dashboard`]),
  };
}

export { MANUAL_STEPS };
